import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  IconTextField,
  Loader,
  PrimaryButton,
} from "widgets";
import { getPhoneConfigForIsd } from "constants/phoneConfig";
import {
  getCountryByCode,
  getDefaultCountry,
  toCountrySelectOptions,
} from "helpers/countryMappings";
import { useCountriesMappings } from "store/backend/countries";
import {
  LOGIN_ARIA_LABELS,
  LOGIN_COPY,
  LOGIN_FIELD_NAMES,
  LOGIN_ICONS,
  LOGIN_INITIAL_VALUES,
  LOGIN_INPUT_TYPES,
  LOGIN_VISIBLE_COUNTRY_CODES,
} from "./constants/loginContent";
import { createLoginValidationSchema } from "./loginValidation";
import useLoginSubmit from "./useLoginSubmit";
import styles from "./Login.module.scss";

const LoginField = ({ name, setApiError, ...fieldProps }) => (
  <Field name={name}>
    {({ field, meta }) => (
      <IconTextField
        id={field.name}
        name={field.name}
        value={field.value}
        onBlur={field.onBlur}
        onChange={(event) => {
          setApiError("");
          field.onChange(event);
        }}
        error={meta.touched && Boolean(meta.error)}
        helperText={meta.touched ? meta.error : undefined}
        {...fieldProps}
      />
    )}
  </Field>
);

const LoginMobileField = ({
  setApiError,
  countryOptions,
  countrySelectDisabled,
  getIsdCodeForCountry,
}) => (
  <Field name={LOGIN_FIELD_NAMES.MOBILE}>
    {({ field, meta, form }) => {
      const countryCode = form.values[LOGIN_FIELD_NAMES.COUNTRY_CODE];
      const isdCode = getIsdCodeForCountry(countryCode);
      const phoneConfig = getPhoneConfigForIsd(isdCode);

      return (
        <IconTextField
          id={field.name}
          name={field.name}
          label={LOGIN_COPY.MOBILE_LABEL}
          type={LOGIN_INPUT_TYPES.TEL}
          placeholder={LOGIN_COPY.MOBILE_PLACEHOLDER}
          value={field.value}
          onBlur={field.onBlur}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched ? meta.error : undefined}
          startSelectValue={countryCode}
          startSelectOptions={countryOptions}
          startSelectDisabled={countrySelectDisabled}
          startSelectAriaLabel={LOGIN_ARIA_LABELS.COUNTRY_CODE}
          inputProps={{
            inputMode: "numeric",
            maxLength: phoneConfig.maxLength,
            autoComplete: "tel-national",
          }}
          onStartSelectChange={(nextCountryCode) => {
            setApiError("");
            form.setFieldValue(LOGIN_FIELD_NAMES.COUNTRY_CODE, nextCountryCode);
            form.setFieldTouched(LOGIN_FIELD_NAMES.COUNTRY_CODE, true, false);
            form.setFieldValue(LOGIN_FIELD_NAMES.MOBILE, "", false);
            form.setFieldTouched(LOGIN_FIELD_NAMES.MOBILE, false, false);
          }}
          onChange={(event) => {
            setApiError("");
            const digitsOnly = (event.target.value || "")
              .replace(/\D/g, "")
              .slice(0, phoneConfig.maxLength);
            form.setFieldValue(LOGIN_FIELD_NAMES.MOBILE, digitsOnly);
          }}
        />
      );
    }}
  </Field>
);

const LoginFormInner = ({
  isValid,
  values,
  setFieldValue,
  apiError,
  setApiError,
  isPending,
  showPassword,
  setShowPassword,
  countries,
  countryOptions,
  countryHelperText,
  isCountriesLoading,
  isCountriesError,
  refetchCountries,
  getIsdCodeForCountry,
}) => {
  const selectedCountryCode = values[LOGIN_FIELD_NAMES.COUNTRY_CODE];

  useEffect(() => {
    if (selectedCountryCode || !countries.length) {
      return;
    }

    const defaultCountry = getDefaultCountry(countries);
    if (defaultCountry?.countryCode) {
      setFieldValue(LOGIN_FIELD_NAMES.COUNTRY_CODE, defaultCountry.countryCode);
    }
  }, [countries, selectedCountryCode, setFieldValue]);

  return (
    <Form className={styles.loginForm}>
      {apiError ? (
        <div className={styles.apiErrorBanner} role="alert">
          {apiError}
        </div>
      ) : null}

      <LoginMobileField
        setApiError={setApiError}
        countryOptions={countryOptions}
        countrySelectDisabled={
          isCountriesLoading || isCountriesError || !countryOptions.length
        }
        getIsdCodeForCountry={getIsdCodeForCountry}
      />

      {countryHelperText ? (
        <div className={styles.countryStatusRow}>
          <span className={styles.countryStatusText}>{countryHelperText}</span>
          {isCountriesError ? (
            <button
              type="button"
              className={styles.countryRetryButton}
              onClick={() => refetchCountries()}
            >
              {LOGIN_COPY.COUNTRY_RETRY}
            </button>
          ) : null}
        </div>
      ) : null}

      <LoginField
        name={LOGIN_FIELD_NAMES.PASSWORD}
        setApiError={setApiError}
        label={LOGIN_COPY.PASSWORD_LABEL}
        type={
          showPassword ? LOGIN_INPUT_TYPES.TEXT : LOGIN_INPUT_TYPES.PASSWORD
        }
        placeholder={LOGIN_COPY.PASSWORD_PLACEHOLDER}
        startIconName={LOGIN_ICONS.LOCK}
        endIconName={
          showPassword ? LOGIN_ICONS.VISIBILITY_OFF : LOGIN_ICONS.VISIBILITY
        }
        endIconAriaLabel={
          showPassword
            ? LOGIN_ARIA_LABELS.HIDE_PASSWORD
            : LOGIN_ARIA_LABELS.SHOW_PASSWORD
        }
        onEndIconClick={() => setShowPassword((previous) => !previous)}
      />

      <PrimaryButton
        type="submit"
        fullWidth
        disabled={
          !isValid ||
          isPending ||
          isCountriesLoading ||
          !values[LOGIN_FIELD_NAMES.COUNTRY_CODE]
        }
      >
        {isPending ? <Loader variant="inline" /> : LOGIN_COPY.SIGN_IN_BUTTON}
      </PrimaryButton>

      <p className={styles.supportText}>{LOGIN_COPY.SUPPORT_TEXT}</p>
    </Form>
  );
};

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const {
    data: countries = [],
    isPending: isCountriesPending,
    isFetching: isCountriesFetching,
    isError: isCountriesError,
    refetch: refetchCountries,
  } = useCountriesMappings();

  const isCountriesLoading =
    isCountriesPending || (isCountriesFetching && countries.length === 0);

  const loginCountries = useMemo(
    () =>
      countries.filter((country) =>
        LOGIN_VISIBLE_COUNTRY_CODES.includes(country.countryCode),
      ),
    [countries],
  );

  const getIsdCodeForCountry = useCallback(
    (countryCode) =>
      getCountryByCode(loginCountries, countryCode)?.isdCode || "",
    [loginCountries],
  );

  const validationSchema = useMemo(
    () => createLoginValidationSchema(getIsdCodeForCountry),
    [getIsdCodeForCountry]
  );

  const countryOptions = useMemo(
    () => toCountrySelectOptions(loginCountries),
    [loginCountries],
  );

  const { handleSubmit, isPending } = useLoginSubmit({
    setApiError,
    getIsdCodeForCountry,
  });

  let countryHelperText;
  if (isCountriesLoading) {
    countryHelperText = LOGIN_COPY.COUNTRY_LOADING;
  } else if (isCountriesError) {
    countryHelperText = LOGIN_COPY.COUNTRY_LOAD_FAILED;
  } else if (!countryOptions.length) {
    countryHelperText = LOGIN_COPY.COUNTRY_NONE_AVAILABLE;
  }

  return (
    <Formik
      initialValues={LOGIN_INITIAL_VALUES}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, values, setFieldValue }) => (
        <LoginFormInner
          isValid={isValid}
          values={values}
          setFieldValue={setFieldValue}
          apiError={apiError}
          setApiError={setApiError}
          isPending={isPending}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          countries={loginCountries}
          countryOptions={countryOptions}
          countryHelperText={countryHelperText}
          isCountriesLoading={isCountriesLoading}
          isCountriesError={isCountriesError}
          refetchCountries={refetchCountries}
          getIsdCodeForCountry={getIsdCodeForCountry}
        />
      )}
    </Formik>
  );
};

export default LoginForm;
